import React, { useState } from "react";
import ScrollContainer from "react-indiana-drag-scroll";

import { useInView, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { WalletCard } from "components";

export type WalletOverviewSwitchProps = {
  payout: number;
  vestingPayout: number;
  currency: CurrencyCode;
  currentYear: number;
  vestingYears: number[];
  onSelect?: (i: number) => void;
  dataTestId?: string;
};

export const WalletOverviewSwitch = ({ payout, vestingPayout, currency, currentYear, vestingYears, onSelect, dataTestId }: WalletOverviewSwitchProps) => {
  const { t } = useTranslation("points");
  const [selectedCardIndex, setSelectedCardIndex] = useState(0);

  const { ref: currentYearCardRef, inView: currentYearCardInView } = useInView({
    threshold: 0.8,
    triggerOnce: false,
    trackVisibility: true,
    delay: 100,
  });

  const { ref: vestingCardRef, inView: vestingCardInView } = useInView({
    threshold: 0.4,
    triggerOnce: false,
    trackVisibility: true,
    delay: 100,
  });

  const getCaption = () => {
    if (payout > 0) {
      return t("walletOverviewSwitch.paidInYear", { year: currentYear + 1 });
    }

    if (payout < 0) {
      return t("walletOverviewSwitch.noPayoutObligation");
    }

    return t("walletOverviewSwitch.noPayout");
  };

  const handleSelect = (index: number) => {
    setSelectedCardIndex(index);
    onSelect?.(index);
  };

  const handleEndScroll = () => {
    // If second wallet card is partly in view we want to select it,
    // if the first card is completely in view we want to select it
    if (currentYearCardInView && selectedCardIndex !== 0) {
      handleSelect(0);

      const card = document.querySelector(".currentYearCard");
      card?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    } else if (!currentYearCardInView && vestingCardInView && selectedCardIndex !== 1) {
      handleSelect(1);

      const card = document.querySelector(".vestingCard");
      card?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  return (
    <ScrollContainer className="wallet-overview-switch" onEndScroll={handleEndScroll}>
      <WalletCard
        headline={t("walletOverviewSwitch.totalCurrentPayout", { year: currentYear })}
        payout={payout}
        currency={currency}
        caption={getCaption()}
        selected={selectedCardIndex === 0}
        onClick={() => handleSelect(0)}
        // @ts-expect-error ref types don't match
        customRef={currentYearCardRef}
        className="currentYearCard"
        dataTestId={`${dataTestId}-current`}
      />
      <WalletCard
        headline={t("walletOverviewSwitch.totalVestedPayout")}
        payout={vestingPayout}
        currency={currency}
        caption={
          vestingYears.length === 1
            ? t("walletOverviewSwitch.paidInYear", { year: vestingYears[0] })
            : t("walletOverviewSwitch.paidBetweenYears", { firstYear: vestingYears[0], lastYear: vestingYears[vestingYears.length - 1] })
        }
        className="margin-left-125 vestingCard"
        variant="vesting"
        selected={selectedCardIndex === 1}
        onClick={() => handleSelect(1)}
        // @ts-expect-error ref types don't match
        customRef={vestingCardRef}
        dataTestId={`${dataTestId}-future`}
      />
    </ScrollContainer>
  );
};
